import { AddJavascriptToHeader } from '@/services/AoeHelpers.ts'
import { GetCultureName, GetOrderEntryConnectorUrl } from '@/stores/SettingsStore'
import { BrainTreeControlFolderName } from '@/services/PaymentGateways/BraintreeService.ts'
import { RmaPaymentTokenCreate } from '@/api/RMA.ts'
import { useStoreLogger } from '@/logger.ts'
import { handleError } from '@/services/ErrorService.ts'
import type { ComposerTranslation } from 'vue-i18n'
import { aoeBraintreePaypalVaultRma } from '@/components/PaymentGateways/Braintree/AoeBraintreePaypalVaultRma.ts'
import { aoeBraintreePaypalCheckoutRma } from '@/components/PaymentGateways/Braintree/AoeBraintreePaypalCheckoutRma.ts'

export { BrainTreeControlFolderName }

export interface IRmaPaymentMethodParameters {
  PaypalMerchantId?: string
  PayPalFlow?: string
  Check3DS?: boolean | string
  [key: string]: unknown
}

export interface IRmaPaymentViewModel {
  name: string
  systemKeyword: string
  controlFolderName: string
  expanded: boolean
  parameters?: IRmaPaymentMethodParameters
}

// Sets up the BrainTree/PayPal payment gateway for collecting a new RMA payment (e.g. an
// exchange upcharge). This is the self-service equivalent of the admin RMA screen's
// SetupBraintreeForReturns (WebAdministrationVue/src/services/BraintreeService.ts), adapted
// to AOEVue's connector-based API and its own dedicated RMA payment gateway singletons -
// see AoeBraintreeRma.ts for why these aren't shared with the checkout flow.
export async function SetupBraintreeForRmaPayment(
  t: ComposerTranslation,
  rmaNumber: string,
  charge: number,
  currencyId: string,
  viewModels: IRmaPaymentViewModel[],
) {
  const logger = useStoreLogger()
  const braintreePaymentMethod = viewModels.find(
    (vm) => vm.controlFolderName === BrainTreeControlFolderName,
  )
  if (braintreePaymentMethod == null) {
    logger.error('SetupBraintreeForRmaPayment', 'braintreePaymentMethod not found')
    return
  }

  const paypalMerchantId = braintreePaymentMethod.parameters?.PaypalMerchantId
  if (paypalMerchantId) {
    await RmaPaymentTokenCreate({
      rmaNumber,
      systemKeyword: braintreePaymentMethod.systemKeyword,
      charge,
    })
      .then(async (response) => {
        if (response.item.ClientToken) {
          let payPalFlow = braintreePaymentMethod.parameters?.PayPalFlow
          if (!payPalFlow) payPalFlow = 'VAULT'
          const locale = (await GetCultureName()).replace('-', '_')
          logger.debug(
            'SetupBraintreeForRmaPayment',
            'paypalMerchantId:', paypalMerchantId,
            'locale:', locale,
            'payPalFlow:', payPalFlow,
          )

          if (payPalFlow === 'VAULT') {
            aoeBraintreePaypalVaultRma.paypalScriptUrl = `https://www.paypal.com/sdk/js?client-id=${paypalMerchantId}`
            aoeBraintreePaypalVaultRma.braintreeScriptUrl = 'https://js.braintreegateway.com/web/3.129.0/js/paypal-checkout.min.js'
            aoeBraintreePaypalVaultRma.clientToken = response.item.ClientToken
            aoeBraintreePaypalVaultRma.paymentValues.paymentAmount = charge.toString()
            aoeBraintreePaypalVaultRma.createPaymentUrl = await GetOrderEntryConnectorUrl('RmaPaymentCreate')
            aoeBraintreePaypalVaultRma.sourceValues.rmaNumber = rmaNumber
            aoeBraintreePaypalVaultRma.sourceValues.systemKeyword = braintreePaymentMethod.systemKeyword
            aoeBraintreePaypalVaultRma.Initialize()
          } else {
            aoeBraintreePaypalCheckoutRma.paypalScriptUrl = `https://www.paypal.com/sdk/js?client-id=${paypalMerchantId}`
            aoeBraintreePaypalCheckoutRma.braintreeScriptUrl = 'https://js.braintreegateway.com/web/3.129.0/js/paypal-checkout.min.js'
            aoeBraintreePaypalCheckoutRma.clientToken = response.item.ClientToken
            aoeBraintreePaypalCheckoutRma.paymentValues.paymentAmount = charge.toString()
            aoeBraintreePaypalCheckoutRma.paymentValues.currencyCode = currencyId
            aoeBraintreePaypalCheckoutRma.createPaymentUrl = await GetOrderEntryConnectorUrl('RmaPaymentCreate')
            aoeBraintreePaypalCheckoutRma.sourceValues.rmaNumber = rmaNumber
            aoeBraintreePaypalCheckoutRma.sourceValues.systemKeyword = braintreePaymentMethod.systemKeyword
            aoeBraintreePaypalCheckoutRma.Initialize()
          }
        } else {
          logger.error('SetupBraintreeForRmaPayment', 'response:', response)
        }
      })
      .catch(async (error) => {
        await handleError(error, t)
      })
  }

  AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/client.min.js')
  AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/three-d-secure.min.js')
  AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/apple-pay.min.js')
  AddJavascriptToHeader('https://js.braintreegateway.com/web/dropin/1.45.2/js/dropin.min.js')
  AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/hosted-fields.js')
  AddJavascriptToHeader('https://www.paypal.com/sdk/js?client-id=AdcdhkfMdglSOykb08jG4z7ezyILtA0YKgiuvHmZOLt8Ae43kMckwN_CgsmLMYE5xOPWxid6YG_JMMZa')
  AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/paypal-checkout.min.js')
}
